<template>
	<BlockVisibilityObserver title="HOW_IT_WORKS">
		<section class="kova-v2-steps">
			<div class="kova-v2-steps__inner">
				<h2 class="kova-v2-steps__title">{{ title }}</h2>

				<ol class="kova-v2-steps__list">
					<li v-for="(step, index) in steps" :key="step.label" class="kova-v2-steps__card">
						<div class="kova-v2-steps__copy">
							<span class="kova-v2-steps__label">{{ step.label }}</span>
							<div class="kova-v2-steps__text">
								<h3 class="kova-v2-steps__name">{{ step.title }}</h3>
								<p class="kova-v2-steps__description">{{ step.text }}</p>
							</div>
						</div>

						<picture v-if="STEP_ART[index]" class="kova-v2-steps__art">
							<source
								:height="STEP_ART[index].desktop.height"
								media="(min-width: 1024px)"
								:srcset="STEP_ART[index].desktop.src"
								:width="STEP_ART[index].desktop.width"
							/>
							<img
								alt=""
								:height="STEP_ART[index].mobile.height"
								decoding="async"
								loading="lazy"
								:src="STEP_ART[index].mobile.src"
								:style="artSize(STEP_ART[index])"
								:width="STEP_ART[index].mobile.width"
							/>
						</picture>
					</li>
				</ol>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import { LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import step1Desktop from '@/assets/kova/landing/v2/step-1-eligibility-desktop.webp'
import step1Mobile from '@/assets/kova/landing/v2/step-1-eligibility-mobile.webp'
import step2Desktop from '@/assets/kova/landing/v2/step-2-clinician-review-desktop.webp'
import step2Mobile from '@/assets/kova/landing/v2/step-2-clinician-review-mobile.webp'
import step3Desktop from '@/assets/kova/landing/v2/step-3-medication-desktop.webp'
import step3Mobile from '@/assets/kova/landing/v2/step-3-medication-mobile.webp'
import step4Desktop from '@/assets/kova/landing/v2/step-4-plan-desktop.webp'
import step4Mobile from '@/assets/kova/landing/v2/step-4-plan-mobile.webp'

defineOptions({ name: 'LandingKovaV2HowItWorks' })

defineProps<LandingComponentProps.KovaV2HowItWorks>()

type Shot = { src: string; width: number; height: number }
type StepImage = { desktop: Shot; mobile: Shot }

// The four steps' art (6458:62966 / 6547:16942), white card and bottom fade baked in
// (`HKW_img_*` exports), in step order. Each has its own drawn size (mobile 100x116 /
// 100x134, desktop 144-168 x 192).
const STEP_ART: StepImage[] = [
	{ mobile: { src: step1Mobile, width: 100, height: 116 }, desktop: { src: step1Desktop, width: 166, height: 192 } },
	{ mobile: { src: step2Mobile, width: 100, height: 134 }, desktop: { src: step2Desktop, width: 144, height: 192 } },
	{ mobile: { src: step3Mobile, width: 100, height: 134 }, desktop: { src: step3Desktop, width: 144, height: 192 } },
	{ mobile: { src: step4Mobile, width: 100, height: 134 }, desktop: { src: step4Desktop, width: 168, height: 192 } },
]

function artSize(image: StepImage) {
	return {
		'--art-m-w': `${image.mobile.width}px`,
		'--art-m-h': `${image.mobile.height}px`,
		'--art-d-w': `${image.desktop.width}px`,
		'--art-d-h': `${image.desktop.height}px`,
	}
}
</script>

<style lang="scss" scoped>
// Mobile (6458:62966): 16 top, 32 bottom, 16px gutters, heading 24px above the cards.
.kova-v2-steps {
	font-family: var(--kova-lp-font);
}

.kova-v2-steps__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-16) var(--size-16) var(--size-32);
}

.kova-v2-steps__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: var(--size-36); // 32px × 1.12 = 35.84px — exact size not in design system
	letter-spacing: -0.64px;
	text-align: center;

	color: var(--kova-lp-ink);
}

.kova-v2-steps__list {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	margin: 0;
	padding: 0;

	list-style: none;
}

// 6458:62972 — white card, art bottom-right, 16px padding except at the bottom.
.kova-v2-steps__card {
	overflow: hidden;

	display: flex;
	align-items: flex-end;
	gap: var(--size-8);
	padding: var(--size-16) var(--size-16) 0;

	border-radius: var(--size-24);
	background: var(--Fill-White);
}

.kova-v2-steps__copy {
	display: flex;
	flex: 1 1 0;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--size-12);
	min-width: 0;
	padding-bottom: var(--size-16);
}

// 6458:62974 — Inter Bold 10/12, uppercase, on a grey pill.
.kova-v2-steps__label {
	padding: var(--size-4) var(--size-8);

	font-family: var(--font-family);
	font-size: var(--size-10);
	font-weight: 700;
	line-height: var(--size-12);
	letter-spacing: -0.1px;
	text-transform: uppercase;

	border-radius: var(--size-24);
	color: var(--kova-lp-highlight);
	background: var(--kova-v2-step-pill);
}

.kova-v2-steps__text {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
}

.kova-v2-steps__name {
	margin: 0;

	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-22); // 16px × 1.36 = 21.76px — exact size not in design system
	letter-spacing: -0.16px;

	color: var(--kova-lp-ink);
}

.kova-v2-steps__description {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	color: var(--Text-Primary-100);
}

.kova-v2-steps__art {
	flex-shrink: 0;
	display: block;

	img {
		display: block;
		width: var(--art-m-w);
		height: var(--art-m-h);
	}
}

// Desktop (6547:16942): 60px block padding, four 298px cards in a 1216 row that starts at
// x=120 — Figma's 16px right-side asymmetry is kept at 1440, not as page overflow.
@media (min-width: 1024px) {
	.kova-v2-steps__inner {
		box-sizing: border-box;
		gap: var(--size-48);
		max-width: 1440px;
		margin-inline: auto;
		padding: var(--size-60) var(--size-40);
	}

	.kova-v2-steps__title {
		align-self: center;
		max-width: 830px;

		font-size: var(--size-56);
		line-height: var(--size-60); // 56px × 1.1 = 61.60px — exact size not in design system
		letter-spacing: -1.12px;
	}

	.kova-v2-steps__list {
		flex-direction: row;
		align-items: stretch;
	}

	// 6547:18035 — 24/24 padding, art centred at the bottom.
	.kova-v2-steps__card {
		flex: 1 1 0;
		flex-direction: column;
		align-items: center;
		min-width: 0;
		padding: var(--size-24) var(--size-24) 0;

		border-radius: var(--size-32);
	}

	.kova-v2-steps__copy {
		flex: 1 0 auto;
		align-self: stretch;
		gap: var(--size-16);
	}

	.kova-v2-steps__label {
		font-size: var(--size-12);
		line-height: var(--size-14);
		letter-spacing: -0.12px;
	}

	.kova-v2-steps__text {
		gap: var(--size-8);
	}

	.kova-v2-steps__name {
		font-size: var(--size-24);
		line-height: var(--size-32); // 24px × 1.32 = 31.68px — exact size not in design system
		letter-spacing: -0.48px;
	}

	.kova-v2-steps__description {
		font-size: var(--size-16);
		line-height: var(--size-22);

		color: var(--kova-v2-text-secondary);
	}

	.kova-v2-steps__art img {
		width: var(--art-d-w);
		height: var(--art-d-h);
	}
}

@media (min-width: 1440px) {
	.kova-v2-steps__inner {
		padding-inline: var(--size-120) var(--size-100); // Closest to 104px right in Figma 6547:16942.
	}
}
</style>
